import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/api';
import { transactionsService } from '../../services/transactionsService';
import './PricingHistoryPage.css';

export const canViewPricingLogs = (user) => user?.email?.trim().toLowerCase() === 'masterte0@tikwiz.com';
const dateTime = (value) => value ? new Date(value).toLocaleString() : '—';

export const orderLogState = (run, item) => {
  if (item.state === 'pending') return run.status === 'failed' ? 'Not attempted' : 'Waiting';
  if (item.state === 'failed') return 'Failed';
  if (item.state === 'running') return 'Started — outcome not recorded';
  if (run.status === 'failed') return `${item.action} — rolled back`;
  if (run.status === 'running') return `${item.action} — not committed`;
  if (run.mode === 'dry_run') return `${item.action} — preview only`;
  return `${item.action}${run.undone_at ? ' — undone' : ''}`;
};

export default function PricingHistoryPage() {
  const [access, setAccess] = useState('loading');
  const [search, setSearch] = useSearchParams();
  const runId = search.get('run');
  const [page, setPage] = useState(0);
  const [itemPage, setItemPage] = useState(0);
  const [history, setHistory] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    authService.getCurrentUser(authService.getToken()).then((user) => {
      if (active) setAccess(canViewPricingLogs(user) ? 'allowed' : 'denied');
    }).catch(() => { if (active) setAccess('denied'); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (access !== 'allowed') return undefined;
    let active = true;
    setLoading(true);
    setError('');
    setDetail(null);
    const request = runId
      ? transactionsService.getOrderPricingLog(runId, itemPage * 100)
      : transactionsService.getOrderPricingHistory(page * 25);
    request.then((result) => {
      if (!active) return;
      if (!result.success) setError(result.error);
      else if (runId) setDetail(result.data);
      else setHistory(result.data);
      setLoading(false);
    });
    return () => { active = false; };
  }, [access, runId, page, itemPage, refresh]);

  return (
    <main className="pricing-history">
      <header>
        <h1>Order pricing history</h1>
        <Link to="/user/transactions/orders">Back to orders</Link>
      </header>
      {access === 'loading' && <p>Checking access...</p>}
      {access === 'denied' && <p role="alert">Pricing log access is restricted.</p>}
      {access === 'allowed' && <>
        <div className="pricing-history-actions">
          {runId && <button onClick={() => { setSearch({}); setItemPage(0); }}>All runs</button>}
          <button onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Refresh</button>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading && <p role="status">Loading pricing logs...</p>}
        {!loading && !error && !runId && history && <>
          <p>Dry runs and applied runs, newest first. Open a run to view each order’s result.</p>
          <div className="pricing-history-table"><table>
            <thead><tr><th>Started</th><th>Mode</th><th>Status</th><th>Started by</th><th>Orders</th><th>Logs</th></tr></thead>
            <tbody>{history.runs.map((run) => <tr key={run.run_id}>
              <td>{dateTime(run.started_at)}</td><td>{run.mode === 'dry_run' ? 'Dry run' : 'Apply'}</td>
              <td>{run.undone_at ? 'Undone' : run.status}</td><td>{run.initiated_by_email || 'Unknown'}</td>
              <td>{run.legacy ? run.legacy_processed_count : run.order_count}</td>
              <td><button onClick={() => { setItemPage(0); setSearch({ run: run.run_id }); }}>View logs</button></td>
            </tr>)}</tbody>
          </table></div>
          {!history.total && <p>No pricing runs have been recorded yet.</p>}
          <nav aria-label="Run pages">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous runs</button>
            <span>{history.total} runs · Page {page + 1}</span>
            <button disabled={(page + 1) * 25 >= history.total} onClick={() => setPage(page + 1)}>Next runs</button>
          </nav>
        </>}
        {!loading && !error && detail && <>
          <h2>{detail.run.mode === 'dry_run' ? 'Dry run' : 'Apply'} · {detail.run.status}</h2>
          <p>Reference: {detail.run.run_id}</p>
          <p>Started {dateTime(detail.run.started_at)} by {detail.run.initiated_by_email || 'Unknown'} · Finished {dateTime(detail.run.completed_at)}</p>
          {detail.run.undone_at && <p>This run was undone on {dateTime(detail.run.undone_at)}.</p>}
          {detail.run.status === 'failed' && <p role="alert">This run failed. Pricing changes were rolled back. Orders not reached are marked “Not attempted”.</p>}
          {detail.run.mode === 'dry_run' && <p>Preview only: pricing changes were not committed.</p>}
          {detail.run.status === 'running' && <p>No final outcome has been recorded. The run may still be active or may have been interrupted. Refresh to check.</p>}
          {detail.run.legacy && <p>Historical run: only previously retained results are available. Older per-order details and tracebacks cannot be recovered.</p>}
          {detail.run.error_traceback && <pre>{detail.run.error_traceback}</pre>}
          {detail.items.map((item) => <article key={item.sequence}>
            <h3>{item.tikwiz_order_id}{item.order_id ? ` · ${item.order_id}` : ''}</h3>
            <strong>{orderLogState(detail.run, item)}</strong>
            {item.message && <p>{item.message}</p>}
            <p>Started {dateTime(item.started_at)} · Finished {dateTime(item.completed_at)}</p>
            {item.input_details && <dl>
              <dt>Requested event / date</dt><dd>{item.input_details.event_name || '—'} / {item.input_details.event_date || '—'}</dd>
              <dt>Requested venue / ticket type</dt><dd>{item.input_details.venue_name || '—'} / {item.input_details.type_label || '—'}</dd>
              <dt>Requested quantity / unit price</dt><dd>{item.input_details.quantity ?? '—'} / {item.input_details.unit_price ?? '—'}</dd>
            </dl>}
            {item.details && <dl>
              <dt>Matched position</dt><dd>{item.details.source_uuid || '—'}</dd>
              <dt>Matched event</dt><dd>{item.details.event_name || item.matched_event_name || '—'}</dd>
              <dt>Units claimed / created</dt><dd>{item.details.units_claimed || 0} / {item.details.units_created || 0}</dd>
              <dt>Units</dt><dd>{item.details.unit_codes?.join(', ') || '—'}</dd>
            </dl>}
            {item.error_traceback && <pre>{item.error_traceback}</pre>}
          </article>)}
          {!detail.total && <p>No per-order logs are available for this run.</p>}
          <nav aria-label="Order log pages">
            <button disabled={itemPage === 0} onClick={() => setItemPage(itemPage - 1)}>Previous orders</button>
            <span>{detail.total} order logs · Page {itemPage + 1}</span>
            <button disabled={(itemPage + 1) * 100 >= detail.total} onClick={() => setItemPage(itemPage + 1)}>Next orders</button>
          </nav>
        </>}
      </>}
    </main>
  );
}
